import React, { useState, useEffect, useRef } from 'react';

/**
 * StreamingText Component - ChatGPT-like typewriter effect
 * Smoothly reveals text character by character as chunks arrive
 * @param {string} text - The full accumulated text to display
 * @param {number} speed - Characters per second (default: 50)
 * @param {boolean} isStreaming - Whether streaming is active
 * @param {string} className - Additional CSS classes
 */
const StreamingText = ({ text = '', speed = 50, isStreaming = true, className = '' }) => {
    const [displayedText, setDisplayedText] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);
    const animationRef = useRef(null);
    const lastUpdateRef = useRef(Date.now());
    const textRef = useRef(text);

    useEffect(() => {
        textRef.current = text;
    }, [text]);

    useEffect(() => {
        if (!isStreaming && displayedText !== text) {
            // When streaming stops, immediately show all remaining text
            setDisplayedText(text);
            setCurrentIndex(text.length);
            return;
        }

        if (!isStreaming) {
            return;
        }

        // Typewriter animation loop
        const animate = () => {
            const now = Date.now();
            const deltaTime = now - lastUpdateRef.current;
            const charsToAdd = Math.floor((deltaTime / 1000) * speed);

            if (charsToAdd > 0) {
                setCurrentIndex(prevIndex => {
                    const newIndex = Math.min(prevIndex + charsToAdd, textRef.current.length);
                    setDisplayedText(textRef.current.slice(0, newIndex));
                    return newIndex;
                });
                lastUpdateRef.current = now;
            }

            if (currentIndex < textRef.current.length) {
                animationRef.current = requestAnimationFrame(animate);
            }
        };

        // Start animation if we have more text to show
        if (currentIndex < text.length) {
            animationRef.current = requestAnimationFrame(animate);
        }

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [text, currentIndex, isStreaming, speed]);

    // Reset when text becomes empty
    useEffect(() => {
        if (!text) {
            setDisplayedText('');
            setCurrentIndex(0);
        }
    }, [text]);

    return (
        <div className={className}>
            <p className="text-[#181D27] text-sm whitespace-pre-wrap font-normal leading-relaxed">
                {displayedText}
                {isStreaming && currentIndex < textRef.current.length && (
                    <span className="inline-block w-[2px] h-[16px] bg-[#279964] ml-1 animate-pulse"></span>
                )}
            </p>
        </div>
    );
};

export default StreamingText;
